import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Visibility,
  MarkEmailRead,
  Reply,
  Search,
  Email,
  Phone,
  Person,
  FiberNew,
} from '@mui/icons-material';
import contactService, { ContactInquiry } from '../../services/contactService';

const statusConfig: Record<ContactInquiry['status'], { label: string; color: 'error' | 'warning' | 'success' }> = {
  new:     { label: 'New',     color: 'error' },
  read:    { label: 'Read',    color: 'warning' },
  replied: { label: 'Replied', color: 'success' },
};

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<ContactInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<ContactInquiry | null>(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await contactService.getContacts({
        page: pagination.page,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      setContacts(res.data || []);
      setPagination(p => ({ ...p, total: res.pagination?.total || 0, pages: res.pagination?.pages || 1 }));
    } catch {
      setError('Failed to load contact inquiries.');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, statusFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleStatusChange = async (id: string, status: ContactInquiry['status']) => {
    try {
      await contactService.updateStatus(id, status);
      setContacts(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      if (selected?.id === id) setSelected(s => s ? { ...s, status } : s);
    } catch {
      setError('Failed to update status.');
    }
  };

  const filtered = contacts.filter(c =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search)
  );

  const newCount = contacts.filter(c => c.status === 'new').length;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Contact Inquiries</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Messages submitted through the Triveni website
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchContacts} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total', value: pagination.total, icon: <Email />, color: '#4361EE' },
          { label: 'New', value: newCount, icon: <FiberNew />, color: '#EF4444' },
          { label: 'Read', value: contacts.filter(c => c.status === 'read').length, icon: <MarkEmailRead />, color: '#F59E0B' },
          { label: 'Replied', value: contacts.filter(c => c.status === 'replied').length, icon: <Reply />, color: '#10B981' },
        ].map(card => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '12px !important' }}>
                <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          placeholder="Search name, email, phone…"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="read">Read</MenuItem>
            <MenuItem value="replied">Replied</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email Sent</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No contact inquiries found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(c => (
                <TableRow key={c.id} hover sx={{ '&:hover': { cursor: 'pointer' } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <a href={`mailto:${c.email}`} style={{ color: '#4361EE', textDecoration: 'none' }}>{c.email}</a>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.phone || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap title={c.message}>{c.message}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusConfig[c.status].label}
                      color={statusConfig[c.status].color}
                      size="small"
                      variant={c.status === 'new' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.email_sent ? 'Sent' : 'Failed'}
                      color={c.email_sent ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => { setSelected(c); handleStatusChange(c.id, c.status === 'new' ? 'read' : c.status); }}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {c.status !== 'replied' && (
                        <Tooltip title="Mark as replied">
                          <IconButton size="small" color="success" onClick={() => handleStatusChange(c.id, 'replied')}>
                            <Reply fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {c.status === 'new' && (
                        <Tooltip title="Mark as read">
                          <IconButton size="small" color="warning" onClick={() => handleStatusChange(c.id, 'read')}>
                            <MarkEmailRead fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination info */}
      <Box mt={1.5} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Showing {filtered.length} of {pagination.total} inquiries
        </Typography>
        <Box display="flex" gap={1}>
          <Button size="small" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
            Previous
          </Button>
          <Button size="small" disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
            Next
          </Button>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={700}>Contact Inquiry</Typography>
                <Chip label={statusConfig[selected.status].label} color={statusConfig[selected.status].color} size="small" />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={1} alignItems="center">
                  <Person fontSize="small" color="action" />
                  <Typography variant="body1" fontWeight={600}>{selected.name}</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">
                    <a href={`mailto:${selected.email}`} style={{ color: '#4361EE' }}>{selected.email}</a>
                  </Typography>
                </Box>
                {selected.phone && (
                  <Box display="flex" gap={1} alignItems="center">
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{selected.phone}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>MESSAGE</Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{selected.message}</Typography>
                  </Paper>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Submitted: {new Date(selected.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                  &nbsp;·&nbsp;
                  Email {selected.email_sent ? 'sent ✓' : 'failed ✗'}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              {selected.status !== 'replied' && (
                <Button
                  startIcon={<Reply />}
                  variant="contained"
                  color="success"
                  onClick={() => handleStatusChange(selected.id, 'replied')}
                >
                  Mark Replied
                </Button>
              )}
              <Button href={`mailto:${selected.email}?subject=Re: Your Triveni inquiry`} variant="outlined" startIcon={<Email />}>
                Reply via Email
              </Button>
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ContactsPage;
